// src/pages/BizKimizPage.js
import React from "react";
import GlobalStyles from "@mui/material/GlobalStyles";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import CollectionsIcon from "@mui/icons-material/Collections";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupsIcon from "@mui/icons-material/Groups";
import HubIcon from "@mui/icons-material/Hub";
import LanguageIcon from "@mui/icons-material/Language";
import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

import { motion } from "framer-motion";
import ResimUpload from "../ResimUpload";
import { API_BASE } from "../config";

/* ========================================================================
   TASARIM SABİTLERİ
   ======================================================================== */

const COLORS = {
  navy: "#0b1f3a",
  navySoft: "#15345d",
  blue: "#2563eb",
  blueSoft: "#eff6ff",
  orange: "#f59e0b",
  orangeSoft: "#fff7ed",
  green: "#10b981",
  text: "#0f172a",
  textSoft: "#64748b",
  page: "#f3f6fb",
  card: "#ffffff",
  border: "#e2e8f0",
};

const HERO_GRADIENT =
  "linear-gradient(125deg, #071a33 0%, #123b6d 48%, #1f5aa6 100%)";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    backgroundColor: "#fff",
    transition: "box-shadow .2s ease, border-color .2s ease",

    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },

    "&.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(37,99,235,.09)",
    },
  },

  "& .MuiInputLabel-root": {
    fontWeight: 600,
  },
};

/* MUI sürümünden bağımsız, gerçek responsive kolon sistemi. */
const cssGrid = (columns, gap = 2) => ({
  display: "grid",
  gridTemplateColumns: columns,
  gap,
  width: "100%",
  maxWidth: "none",
  margin: "0 !important",
  alignItems: "stretch",

  "& > .MuiGrid-root": {
    width: "100% !important",
    maxWidth: "none !important",
    minWidth: "0 !important",
    padding: "0 !important",
  },
});

/* ========================================================================
   DİL METİNLERİ
   ======================================================================== */

const ALLOWED = ["tr", "en", "de"];

const T = {
  tr: {
    title: "BİZ KİMİZ",
    subtitle: "Hikayemiz, değerlerimiz ve kurumsal yapımız",
    contentLanguage: "İçerik dili",

    editorIntro:
      "Kurumsal bilgilerinizi, ekibinizi ve galerinizi tek ekrandan düzenleyin.",

    previewIntro: "Kaydetmeden önce sayfanızın görünümünü kontrol edin.",

    metrics: {
      teams: "Faaliyet Sayısı",
      trophies: "Başarı & Ödül",
      members: "Topluluk & Üye Sayısı",
      years: "Yıl & Deneyim",
    },

    blocks: {
      mission: "Misyon",
      vision: "Vizyon",
      history: "Biz Kimiz",
      counters: "Başarı Göstergeleri",
      people: "Kişiler / Görevliler",
      gallery: "Galeri",
      controls: "Canlı İçerik Ayarları",
      preview: "Kurumsal Sayfa Önizlemesi",
    },

    fields: {
      mission: "Misyon metni",
      vision: "Vizyon metni",
      history: "Hikayemiz / Hakkımızda metni",
      teams: "Faaliyet Sayısı",
      trophies: "Başarı & Ödül",
      members: "Topluluk & Üye Sayısı",
      years: "Yıl & Deneyim",
      personName: "Ad Soyad",
      personRole: "Görev / Yetki",
      personDesc: "Açıklama",
      personLink: "Fotoğraf Linki",
      galleryName: "Görsel Adı",
      galleryDesc: "Açıklama",
      galleryLink: "Görsel Linki",
    },

    labels: {
      active: "Aktif",
      passive: "Pasif",
      addPerson: "Yeni Kişi Ekle",
      addPhoto: "Yeni Görsel Ekle",
      save: "Tüm Değişiklikleri Kaydet",
      copy: "Linki kopyala",
      saved: "Verileriniz başarıyla kaydedildi.",
      failed: "Kayıt başarısız. Lütfen tekrar deneyin.",
      loading: "Kaydediliyor...",
      delete: "Sil",
      galleryImage: "Galeri görseli",
      emptyPeople: "Henüz kişi eklenmedi.",
      emptyGallery: "Henüz galeri görseli eklenmedi.",
    },
  },

  en: {
    title: "WHO WE ARE",
    subtitle: "Our story, values and corporate structure",
    contentLanguage: "Content language",

    editorIntro:
      "Manage your corporate information, team and gallery from one screen.",

    previewIntro: "Review how your page looks before saving.",

    metrics: {
      teams: "Activity Count",
      trophies: "Achievements & Awards",
      members: "Community & Members",
      years: "Years & Experience",
    },

    blocks: {
      mission: "Mission",
      vision: "Vision",
      history: "Who We Are",
      counters: "Achievement Indicators",
      people: "People / Officials",
      gallery: "Gallery",
      controls: "Live Content Settings",
      preview: "Corporate Page Preview",
    },

    fields: {
      mission: "Mission text",
      vision: "Vision text",
      history: "Our story / About us text",
      teams: "Activity Count",
      trophies: "Achievements & Awards",
      members: "Community & Members",
      years: "Years & Experience",
      personName: "Full Name",
      personRole: "Role / Authority",
      personDesc: "Description",
      personLink: "Photo Link",
      galleryName: "Image Name",
      galleryDesc: "Description",
      galleryLink: "Image Link",
    },

    labels: {
      active: "Active",
      passive: "Inactive",
      addPerson: "Add New Person",
      addPhoto: "Add New Image",
      save: "Save All Changes",
      copy: "Copy link",
      saved: "Your data was saved successfully.",
      failed: "Save failed. Please try again.",
      loading: "Saving...",
      delete: "Delete",
      galleryImage: "Gallery image",
      emptyPeople: "No people have been added yet.",
      emptyGallery: "No gallery images have been added yet.",
    },
  },

  de: {
    title: "WER WIR SIND",
    subtitle: "Unsere Geschichte, Werte und Unternehmensstruktur",
    contentLanguage: "Inhaltssprache",

    editorIntro:
      "Verwalten Sie Unternehmensinformationen, Team und Galerie auf einer Seite.",

    previewIntro: "Prüfen Sie die Seitenansicht vor dem Speichern.",

    metrics: {
      teams: "Anzahl der Aktivitäten",
      trophies: "Erfolge & Auszeichnungen",
      members: "Community & Mitglieder",
      years: "Jahre & Erfahrung",
    },

    blocks: {
      mission: "Mission",
      vision: "Vision",
      history: "Wer Wir Sind",
      counters: "Erfolgskennzahlen",
      people: "Personen / Verantwortliche",
      gallery: "Galerie",
      controls: "Live-Inhalt Einstellungen",
      preview: "Vorschau der Unternehmensseite",
    },

    fields: {
      mission: "Missionstext",
      vision: "Visionstext",
      history: "Geschichte / Über-uns-Text",
      teams: "Anzahl der Aktivitäten",
      trophies: "Erfolge & Auszeichnungen",
      members: "Community & Mitglieder",
      years: "Jahre & Erfahrung",
      personName: "Vollständiger Name",
      personRole: "Rolle / Befugnis",
      personDesc: "Beschreibung",
      personLink: "Foto-Link",
      galleryName: "Bildname",
      galleryDesc: "Beschreibung",
      galleryLink: "Bild-Link",
    },

    labels: {
      active: "Aktiv",
      passive: "Inaktiv",
      addPerson: "Neue Person hinzufügen",
      addPhoto: "Neues Bild hinzufügen",
      save: "Alle Änderungen speichern",
      copy: "Link kopieren",
      saved: "Ihre Daten wurden erfolgreich gespeichert.",
      failed: "Speichern fehlgeschlagen. Bitte erneut versuchen.",
      loading: "Speichern...",
      delete: "Löschen",
      galleryImage: "Galeriebild",
      emptyPeople: "Noch keine Person hinzugefügt.",
      emptyGallery: "Noch kein Galeriebild hinzugefügt.",
    },
  },
};

/* ========================================================================
   DİL KONTROLÜ
   ======================================================================== */

function useUiTexts() {
  const [uiLang, setUiLang] = React.useState(() =>
    (localStorage.getItem("app_lang") || "tr").slice(0, 2).toLowerCase(),
  );

  React.useEffect(() => {
    const sync = () => {
      const nextLang = (localStorage.getItem("app_lang") || "tr")
        .slice(0, 2)
        .toLowerCase();

      setUiLang(ALLOWED.includes(nextLang) ? nextLang : "tr");
    };

    window.addEventListener("app_lang_change", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);

    return () => {
      window.removeEventListener("app_lang_change", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  const lang = ALLOWED.includes(uiLang) ? uiLang : "tr";

  return {
    TT: T[lang],
    lang,
  };
}

/* ========================================================================
   VARSAYILAN İÇERİK VE LOCAL STORAGE
   ======================================================================== */

const defaults = {
  mission: "",
  missionActive: true,

  vision: "",
  visionActive: true,

  history: "",
  historyActive: true,

  countersActive: true,

  counters: {
    teams: 0,
    trophies: 0,
    members: 0,
    years: 0,
  },

  peopleActive: true,
  people: [],

  galleryActive: true,
  gallery: [],
};

const STORAGE_KEY = "about_content_by_lang_v6";

function loadByLang(lang) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = raw ? JSON.parse(raw) : {};

    return {
      ...all,

      [lang]: {
        ...defaults,
        ...(all?.[lang] || {}),
      },
    };
  } catch {
    return {
      [lang]: defaults,
    };
  }
}

function saveByLang(lang, data) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = raw ? JSON.parse(raw) : {};

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...all,
        [lang]: data,
      }),
    );
  } catch {
    // Local storage çalışmasa bile sunucu kaydı devam eder.
  }
}

/* ========================================================================
   API YARDIMCILARI
   MEVCUT ENDPOINT VE TOKEN YAPISI AYNEN KORUNDU
   ======================================================================== */

const bust = (url, version) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${version}` : "";

const authHeader = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const api = (path, options = {}) =>
  fetch(`${API_BASE}${path}`, {
    ...options,

    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
      ...(options.headers || {}),
    },
  });

const MotionButton = motion(Button);
const MotionPaper = motion(Paper);
const MotionImg = motion("img");

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) {
    return "?";
  }

  return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase();
}

/* ========================================================================
   DURUM ANAHTARI
   ======================================================================== */

function StatusSwitch({ checked, onChange, TT }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
      <Chip
        size="small"
        icon={checked ? <CheckCircleIcon /> : undefined}
        label={checked ? TT.labels.active : TT.labels.passive}
        sx={{
          display: {
            xs: "none",
            sm: "inline-flex",
          },

          fontWeight: 800,

          color: checked ? "#047857" : COLORS.textSoft,

          bgcolor: checked ? "#ecfdf5" : "#f1f5f9",

          "& .MuiChip-icon": {
            color: "#10b981",
          },
        }}
      />

      <Switch checked={checked} onChange={onChange} color="primary" />
    </Stack>
  );
}

/* ========================================================================
   PANEL BAŞLIĞI
   ======================================================================== */

function PanelHeading({ icon, title, subtitle, action }) {
  return (
    <Stack
      direction={{
        xs: "column",
        sm: "row",
      }}
      alignItems={{
        xs: "stretch",
        sm: "center",
      }}
      justifyContent="space-between"
      spacing={2}
      sx={{
        mb: 2.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            color: COLORS.blue,
            bgcolor: COLORS.blueSoft,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box minWidth={0}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              color: COLORS.text,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.4,
                color: COLORS.textSoft,
                lineHeight: 1.5,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      {action && <Box flexShrink={0}>{action}</Box>}
    </Stack>
  );
}

/* ========================================================================
   MİSYON / VİZYON / BİZ KİMİZ DÜZENLEME KARTI
   ======================================================================== */

function ContentEditorCard({
  title,
  value,
  placeholder,
  checked,
  onToggle,
  onChange,
  TT,
  accent,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 0,
        p: {
          xs: 2,
          sm: 2.5,
        },

        borderRadius: 3.5,
        borderColor: COLORS.border,
        height: "100%",
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 8px 24px rgba(15,23,42,.04)",

        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accent,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            color: COLORS.text,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {title}
        </Typography>

        <StatusSwitch checked={checked} onChange={onToggle} TT={TT} />
      </Stack>

      <TextField
        value={value || ""}
        onChange={onChange}
        multiline
        minRows={6}
        fullWidth
        placeholder={placeholder}
        sx={fieldSx}
      />
    </Paper>
  );
}

/* ========================================================================
   ÜST SAYISAL KARTLAR
   ======================================================================== */

function MetricCard({ icon, value, label, gradient }) {
  return (
    <MotionPaper
      whileHover={{
        y: -4,
      }}
      transition={{
        type: "spring",
        stiffness: 220,
        damping: 20,
      }}
      sx={{
        width: "100%",

        minHeight: {
          xs: 132,
          sm: 150,
        },

        p: {
          xs: 1.5,
          sm: 2.25,
        },

        borderRadius: {
          xs: 3,
          sm: 4,
        },

        color: "#fff",
        background: gradient,
        boxShadow: "0 16px 34px rgba(2,12,27,.22)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        overflow: "hidden",
        position: "relative",

        "&::after": {
          content: '""',
          position: "absolute",
          width: 100,
          height: 100,
          borderRadius: "50%",
          right: -34,
          top: -34,
          bgcolor: "rgba(255,255,255,.12)",
        },
      }}
    >
      <Box
        sx={{
          opacity: 0.9,
          position: "relative",
          zIndex: 1,
        }}
      >
        {icon}
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: {
              xs: "1.9rem",
              sm: "2.45rem",
            },

            fontWeight: 950,
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </Typography>

        <Typography
          sx={{
            mt: 1,

            minHeight: {
              xs: 32,
              sm: 36,
            },

            fontWeight: 850,
            textTransform: "uppercase",
            letterSpacing: 0.4,

            fontSize: {
              xs: "0.65rem",
              sm: "0.75rem",
              md: "0.78rem",
            },

            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
      </Box>
    </MotionPaper>
  );
}

/* ========================================================================
   SAYISAL GİRİŞ ALANI
   ======================================================================== */

function MetricInput({ label, value, onChange }) {
  return (
    <TextField
      type="number"
      label={label}
      value={value ?? 0}
      onChange={onChange}
      fullWidth
      inputProps={{
        min: 0,
      }}
      sx={fieldSx}
    />
  );
}

/* ========================================================================
   GÖRSEL YÜKLEME ALANI
   ======================================================================== */

function ImageField({ value, onChange, label, rounded, version, setVersion }) {
  const preview = value ? bust(value, version) : "";

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 3,
        borderColor: COLORS.border,
        bgcolor: "#f8fafc",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        spacing={1.5}
        alignItems={{
          xs: "stretch",
          sm: "center",
        }}
      >
        <Avatar
          variant={rounded ? "rounded" : "circular"}
          src={preview}
          sx={{
            width: {
              xs: 72,
              sm: 82,
            },

            height: {
              xs: 72,
              sm: 82,
            },

            alignSelf: {
              xs: "center",
              sm: "auto",
            },

            bgcolor: "#e2e8f0",
            color: COLORS.textSoft,
            border: "3px solid #fff",
            boxShadow: "0 5px 15px rgba(15,23,42,.12)",
          }}
        >
          {rounded ? <CollectionsIcon /> : <PersonIcon />}
        </Avatar>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: "100%",
          }}
        >
          <Stack spacing={1}>
            <TextField
              size="small"
              fullWidth
              label={label}
              value={value || ""}
              onChange={(event) => onChange(event.target.value)}
              placeholder="https://..."
              sx={fieldSx}
            />

            <Box
              sx={{
                maxWidth: "100%",
                overflow: "hidden",

                "& button": {
                  maxWidth: "100%",
                },
              }}
            >
              <ResimUpload
                label="📤 Yükle"
                defaultPreview={value}
                onUpload={(url) => {
                  onChange(url);

                  setVersion((current) => current + 1);
                }}
              />
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

/* ========================================================================
   KİŞİ DÜZENLEME KARTI
   ======================================================================== */

function PersonEditorCard({
  person,
  index,
  setPersonValue,
  removePerson,
  TT,
  version,
  setVersion,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 0,
        p: {
          xs: 1.75,
          sm: 2.25,
        },

        borderRadius: 3.5,
        borderColor: COLORS.border,
        height: "100%",
        boxShadow: "0 8px 24px rgba(15,23,42,.045)",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: 2,
        }}
      >
        <Chip
          label={`${String(index + 1).padStart(2, "0")} · ${
            person.name || TT.fields.personName
          }`}
          sx={{
            maxWidth: "calc(100% - 50px)",
            fontWeight: 850,
            color: COLORS.navy,
            bgcolor: COLORS.blueSoft,
          }}
        />

        <Tooltip title={TT.labels.delete}>
          <IconButton
            color="error"
            onClick={() => removePerson(index)}
            sx={{
              bgcolor: "#fff1f2",

              "&:hover": {
                bgcolor: "#ffe4e6",
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack spacing={2}>
        <ImageField
          value={person.photoUrl || person.link || ""}
          rounded={false}
          label={TT.fields.personLink}
          version={version}
          setVersion={setVersion}
          onChange={(finalUrl) => {
            setPersonValue(index, "photoUrl", finalUrl);

            setPersonValue(index, "link", finalUrl);
          }}
        />

        <Grid
          container
          spacing={0}
          sx={cssGrid(
            {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            1.5,
          )}
        >
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              label={TT.fields.personName}
              value={person.name || ""}
              onChange={(event) =>
                setPersonValue(index, "name", event.target.value)
              }
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              label={TT.fields.personRole}
              value={person.role || ""}
              onChange={(event) =>
                setPersonValue(index, "role", event.target.value)
              }
              sx={fieldSx}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              gridColumn: {
                xs: "auto",
                sm: "1 / -1",
              },
            }}
          >
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              label={TT.fields.personDesc}
              value={person.desc || ""}
              onChange={(event) =>
                setPersonValue(index, "desc", event.target.value)
              }
              sx={fieldSx}
            />
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
}

/* ========================================================================
   GALERİ DÜZENLEME KARTI
   ======================================================================== */

function GalleryEditorCard({
  item,
  index,
  setGalleryValue,
  removeGalleryItem,
  TT,
  version,
  setVersion,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 0,
        p: {
          xs: 1.75,
          sm: 2.25,
        },

        borderRadius: 3.5,
        borderColor: COLORS.border,
        height: "100%",
        boxShadow: "0 8px 24px rgba(15,23,42,.045)",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: 2,
        }}
      >
        <Chip
          label={`${String(index + 1).padStart(2, "0")} · ${
            item.name || TT.fields.galleryName
          }`}
          sx={{
            maxWidth: "calc(100% - 50px)",
            fontWeight: 850,
            color: "#9a3412",
            bgcolor: COLORS.orangeSoft,
          }}
        />

        <Tooltip title={TT.labels.delete}>
          <IconButton
            color="error"
            onClick={() => removeGalleryItem(index)}
            sx={{
              bgcolor: "#fff1f2",

              "&:hover": {
                bgcolor: "#ffe4e6",
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack spacing={2}>
        <ImageField
          value={item.url || ""}
          rounded
          label={TT.fields.galleryLink}
          version={version}
          setVersion={setVersion}
          onChange={(finalUrl) => setGalleryValue(index, "url", finalUrl)}
        />

        <Grid
          container
          spacing={0}
          sx={cssGrid(
            {
              xs: "minmax(0, 1fr)",
              sm: "minmax(0, 5fr) minmax(0, 7fr)",
            },
            1.5,
          )}
        >
          <Grid item xs={12} sm={5}>
            <TextField
              size="small"
              fullWidth
              label={TT.fields.galleryName}
              value={item.name || ""}
              onChange={(event) =>
                setGalleryValue(index, "name", event.target.value)
              }
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={7}>
            <TextField
              size="small"
              fullWidth
              label={TT.fields.galleryDesc}
              value={item.desc || ""}
              onChange={(event) =>
                setGalleryValue(index, "desc", event.target.value)
              }
              sx={fieldSx}
            />
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
}

/* ========================================================================
   BOŞ LİSTE GÖRÜNÜMÜ
   ======================================================================== */

function EmptyState({ icon, text }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 0,
        py: 5,
        px: 2,
        borderRadius: 3,
        borderStyle: "dashed",
        borderColor: "#cbd5e1",
        textAlign: "center",
        color: COLORS.textSoft,
        bgcolor: "#f8fafc",
      }}
    >
      <Box
        sx={{
          mb: 1,
          opacity: 0.6,
        }}
      >
        {icon}
      </Box>

      <Typography fontWeight={700}>{text}</Typography>
    </Paper>
  );
}

/* ========================================================================
   ÖNİZLEME METİN KARTI
   ======================================================================== */

function PreviewTextCard({ title, text, accent, icon }) {
  return (
    <MotionPaper
      whileHover={{
        y: -3,
      }}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 22,
      }}
      sx={{
        width: "100%",
        minWidth: 0,
        p: {
          xs: 2.25,
          sm: 3,
        },

        borderRadius: 4,
        height: "100%",
        bgcolor: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        boxShadow: "0 12px 32px rgba(15,23,42,.06)",
        position: "relative",
        overflow: "hidden",

        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          height: 4,
          bgcolor: accent,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.2}
        sx={{
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: `${accent}14`,
            color: accent,
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            fontWeight: 950,
            color: COLORS.text,
            textTransform: "uppercase",
            letterSpacing: 0.45,
          }}
        >
          {title}
        </Typography>
      </Stack>

      <Typography
        sx={{
          color: COLORS.text,

          fontSize: {
            xs: "0.95rem",
            sm: "1rem",
          },

          lineHeight: 1.8,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {text || "—"}
      </Typography>
    </MotionPaper>
  );
}

/* ========================================================================
   ANA SAYFA
   ======================================================================== */

export default function BizKimizPage() {
  const { TT, lang } = useUiTexts();

  const [openImg, setOpenImg] = React.useState(null);

  const [content, setContent] = React.useState(() => loadByLang(lang)[lang]);

  const [version, setVersion] = React.useState(0);

  const [saving, setSaving] = React.useState(false);

  const [snack, setSnack] = React.useState({
    open: false,
    msg: "",
    severity: "success",
  });

  /* DİL DEĞİŞİNCE LOCAL İÇERİĞİ YÜKLE */

  React.useEffect(() => {
    setContent(loadByLang(lang)[lang]);
  }, [lang]);

  /* LOCAL STORAGE OTOMATİK KAYIT */

  const saveTimeout = React.useRef();

  React.useEffect(() => {
    clearTimeout(saveTimeout.current);

    saveTimeout.current = setTimeout(() => saveByLang(lang, content), 400);

    return () => clearTimeout(saveTimeout.current);
  }, [content, lang]);

  /* ======================================================================
     SUNUCUDAN VERİ ÇEK
     GET /bizkimiz?lang=...
     ====================================================================== */

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const response = await api(`/bizkimiz?lang=${lang}`, {
          method: "GET",
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();

        if (!mounted) {
          return;
        }

        const incoming = {
          mission: data.mission ?? "",

          missionActive: data.missionActive ?? true,

          vision: data.vision ?? "",

          visionActive: data.visionActive ?? true,

          history: data.history ?? "",

          historyActive: data.historyActive ?? true,

          countersActive: data.countersActive ?? true,

          peopleActive: data.peopleActive ?? true,

          galleryActive: data.galleryActive ?? true,

          counters: {
            teams: data.counters?.teams ?? 0,

            trophies: data.counters?.trophies ?? 0,

            members: data.counters?.members ?? 0,

            years: data.counters?.years ?? 0,
          },

          people: (data.people || []).map((person) => ({
            name: person.name || "",

            role: person.role || "",

            desc: person.desc || "",

            link: person.photoUrl || person.link || "",

            photoUrl: person.photoUrl || person.link || "",
          })),

          gallery: (data.gallery || []).map((item) => ({
            name: item.name || "",

            desc: item.desc || "",

            url: item.url || "",
          })),
        };

        setContent(incoming);

        saveByLang(lang, incoming);

        setVersion((current) => current + 1);
      } catch {
        // Sunucuya bağlanamazsa local içerikle devam eder.
      }
    })();

    return () => {
      mounted = false;
    };
  }, [lang]);

  /* ======================================================================
     ALAN GÜNCELLEME
     ====================================================================== */

  const onField = (path, value) => {
    setContent((previous) => {
      const next = {
        ...previous,
      };

      const parts = path.split(".");

      let reference = next;

      for (let index = 0; index < parts.length - 1; index += 1) {
        reference[parts[index]] = {
          ...(reference[parts[index]] || {}),
        };

        reference = reference[parts[index]];
      }

      reference[parts[parts.length - 1]] = value;

      return next;
    });
  };

  const copy = (text) => navigator.clipboard?.writeText(text);

  /* ======================================================================
     KİŞİ İŞLEMLERİ
     ====================================================================== */

  const addPerson = () => {
    onField("people", [
      ...(content.people || []),

      {
        name: "",
        role: "",
        desc: "",
        link: "",
        photoUrl: "",
      },
    ]);
  };

  const removePerson = (index) => {
    const people = [...(content.people || [])];

    people.splice(index, 1);

    onField("people", people);
  };

const setPersonValue = (index, key, value) => {
  setContent((previous) => {
    const people = [...(previous.people || [])];

    people[index] = {
      ...(people[index] || {}),
      [key]: value,

      ...(key === "photoUrl" || key === "link"
        ? {
            photoUrl: value,
            link: value,
          }
        : {}),
    };

    return {
      ...previous,
      people,
    };
  });
};
  /* ======================================================================
     GALERİ İŞLEMLERİ
     ====================================================================== */

  const addGalleryItem = () => {
    onField("gallery", [
      ...(content.gallery || []),

      {
        name: "",
        desc: "",
        url: "",
      },
    ]);
  };

  const removeGalleryItem = (index) => {
    const gallery = [...(content.gallery || [])];

    gallery.splice(index, 1);

    onField("gallery", gallery);
  };

  const setGalleryValue = (index, key, value) => {
    const gallery = [...(content.gallery || [])];

    gallery[index] = {
      ...(gallery[index] || {}),
      [key]: value,
    };

    onField("gallery", gallery);
  };

  /* ======================================================================
     SUNUCUYA KAYDET
     PUT /bizkimiz
     MEVCUT PAYLOAD YAPISI KORUNDU
     ====================================================================== */

  const saveToServer = async () => {
    try {
      setSaving(true);

      const payload = {
        lang,

        mission: content.mission || "",

        missionActive: !!content.missionActive,

        vision: content.vision || "",

        visionActive: !!content.visionActive,

        history: content.history || "",

        historyActive: !!content.historyActive,

        countersActive: !!content.countersActive,

        peopleActive: !!content.peopleActive,

        galleryActive: !!content.galleryActive,

        counters: {
          teams: Number(content.counters?.teams) || 0,

          trophies: Number(content.counters?.trophies) || 0,

          members: Number(content.counters?.members) || 0,

          years: Number(content.counters?.years) || 0,
        },

        people: (content.people || []).map((person) => {
          const url = person.photoUrl || person.link || "";

          return {
            name: person.name || "",

            role: person.role || "",

            desc: person.desc || "",

            link: url,
            photoUrl: url,
          };
        }),

        gallery: (content.gallery || []).map((item) => ({
          name: item.name || "",

          desc: item.desc || "",

          url: item.url || "",
        })),
      };

      const response = await api("/bizkimiz", {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("PUT failed");
      }

      await response.json();

      setContent((previous) => ({
        ...previous,
      }));

      saveByLang(lang, {
        ...payload,
      });

      setSnack({
        open: true,
        msg: TT.labels.saved,
        severity: "success",
      });
    } catch {
      setSnack({
        open: true,
        msg: TT.labels.failed,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  /* ======================================================================
     SAYISAL KART TANIMLARI
     ====================================================================== */

  const metricItems = [
    {
      key: "teams",
      icon: <HubIcon />,
      gradient: "linear-gradient(135deg, #4f46e5, #0284c7)",
    },

    {
      key: "trophies",
      icon: <WorkspacePremiumIcon />,
      gradient: "linear-gradient(135deg, #be185d, #e11d48)",
    },

    {
      key: "members",
      icon: <GroupsIcon />,
      gradient: "linear-gradient(135deg, #059669, #14b8a6)",
    },

    {
      key: "years",
      icon: <ScheduleIcon />,
      gradient: "linear-gradient(135deg, #d97706, #f59e0b)",
    },
  ];

  return (
    <>
      <GlobalStyles
        styles={{
          html: {
            overflowX: "hidden",
          },

          body: {
            margin: 0,
            overflowX: "hidden",
            background: COLORS.page,
          },

          "*": {
            boxSizing: "border-box",
          },

          ".MuiSnackbarContent-message": {
            fontSize: "1rem",
            fontWeight: 800,
          },

          ".RaNotification-content": {
            fontSize: "1rem",
            fontWeight: 800,
          },
        }}
      />

      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          overflowX: "hidden",
          bgcolor: COLORS.page,

          pb: {
            xs: 5,
            md: 8,
          },
        }}
      >
        {/* ===============================================================
            HERO
            =============================================================== */}

        <Box
          sx={{
            color: "#fff",
            background: HERO_GRADIENT,
            position: "relative",
            overflow: "hidden",
            borderBottom: "4px solid #f59e0b",

            "&::before": {
              content: '""',
              position: "absolute",

              width: {
                xs: 260,
                md: 500,
              },

              height: {
                xs: 260,
                md: 500,
              },

              borderRadius: "50%",

              right: {
                xs: -150,
                md: -180,
              },

              top: {
                xs: -120,
                md: -260,
              },

              background:
                "radial-gradient(circle, rgba(255,255,255,.16), rgba(255,255,255,0) 68%)",
            },

            "&::after": {
              content: '""',
              position: "absolute",
              width: 260,
              height: 260,
              borderRadius: "50%",
              left: -150,
              bottom: -190,
              bgcolor: "rgba(245,158,11,.18)",
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              position: "relative",
              zIndex: 1,

              pt: {
                xs: 3.5,
                sm: 5,
                md: 6,
              },

              pb: {
                xs: 4,
                sm: 5,
                md: 6,
              },

              px: {
                xs: 2,
                sm: 3,
              },
            }}
          >
            <Grid
              container
              spacing={0}
              alignItems="flex-end"
              sx={cssGrid("minmax(0, 1fr)", 0)}
            >
              <Grid item xs={12} md={8}>
                <Chip
                  icon={<LanguageIcon />}
                  label={`${TT.contentLanguage}: ${lang.toUpperCase()}`}
                  sx={{
                    mb: 2,
                    color: "#fff",
                    fontWeight: 850,
                    bgcolor: "rgba(255,255,255,.12)",
                    border: "1px solid rgba(255,255,255,.22)",
                    backdropFilter: "blur(10px)",

                    "& .MuiChip-icon": {
                      color: "#fbbf24",
                    },
                  }}
                />

                <Typography
                  component="h1"
                  sx={{
                    fontSize: {
                      xs: "2rem",
                      sm: "2.8rem",
                      md: "3.65rem",
                    },

                    fontWeight: 950,
                    lineHeight: 1.04,

                    letterSpacing: {
                      xs: -0.8,
                      md: -1.5,
                    },

                    textTransform: "uppercase",
                  }}
                >
                  {TT.title}
                </Typography>

                <Typography
                  sx={{
                    mt: 1.4,
                    maxWidth: 720,
                    color: "rgba(255,255,255,.82)",

                    fontSize: {
                      xs: "0.95rem",
                      sm: "1.1rem",
                    },

                    lineHeight: 1.6,
                  }}
                >
                  {TT.subtitle}
                </Typography>
              </Grid>
            </Grid>

            {content.countersActive && (
              <Grid
                container
                spacing={0}
                sx={{
                  ...cssGrid(
                    {
                      xs: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                    { xs: 1.25, sm: 2 },
                  ),
                  mt: 2.5,
                }}
              >
                {metricItems.map((item) => (
                  <Grid
                    item
                    xs={6}
                    sm={6}
                    lg={3}
                    key={item.key}
                    sx={{
                      display: "flex",
                      minWidth: 0,
                    }}
                  >
                    <MetricCard
                      value={content.counters?.[item.key] ?? 0}
                      label={TT.metrics[item.key]}
                      icon={item.icon}
                      gradient={item.gradient}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Container>
        </Box>

        {/* ===============================================================
            ANA İÇERİK
            =============================================================== */}

        <Container
          maxWidth={false}
          sx={{
            mt: {
              xs: 2,
              md: 3.5,
            },

            px: {
              xs: 1.5,
              sm: 3,
            },
          }}
        >
          <Paper
            sx={{
              p: {
                xs: 1.75,
                sm: 3,
                md: 3.5,
              },

              borderRadius: {
                xs: 3,
                md: 4.5,
              },

              border: `1px solid ${COLORS.border}`,
              boxShadow: "0 18px 48px rgba(15,23,42,.075)",
              bgcolor: "rgba(255,255,255,.98)",
              overflow: "hidden",
            }}
          >
            <PanelHeading
              icon={<TuneIcon />}
              title={`${TT.blocks.controls} · ${lang.toUpperCase()}`}
              subtitle={TT.editorIntro}
            />

            {/* ===========================================================
                MİSYON / VİZYON / BİZ KİMİZ
                =========================================================== */}

            <Grid
              container
              spacing={0}
              alignItems="stretch"
              sx={cssGrid(
                {
                  xs: "minmax(0, 1fr)",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
                2,
              )}
            >
              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  display: "flex",
                }}
              >
                <ContentEditorCard
                  title={TT.blocks.mission}
                  value={content.mission}
                  placeholder={TT.fields.mission}
                  checked={!!content.missionActive}
                  onToggle={(event) =>
                    onField("missionActive", event.target.checked)
                  }
                  onChange={(event) => onField("mission", event.target.value)}
                  TT={TT}
                  accent={COLORS.blue}
                />
              </Grid>

              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  display: "flex",
                }}
              >
                <ContentEditorCard
                  title={TT.blocks.vision}
                  value={content.vision}
                  placeholder={TT.fields.vision}
                  checked={!!content.visionActive}
                  onToggle={(event) =>
                    onField("visionActive", event.target.checked)
                  }
                  onChange={(event) => onField("vision", event.target.value)}
                  TT={TT}
                  accent={COLORS.orange}
                />
              </Grid>

              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  display: "flex",
                }}
              >
                <ContentEditorCard
                  title={TT.blocks.history}
                  value={content.history}
                  placeholder={TT.fields.history}
                  checked={!!content.historyActive}
                  onToggle={(event) =>
                    onField("historyActive", event.target.checked)
                  }
                  onChange={(event) => onField("history", event.target.value)}
                  TT={TT}
                  accent={COLORS.green}
                />
              </Grid>
            </Grid>

            <Divider
              sx={{
                my: {
                  xs: 3,
                  md: 4,
                },

                borderColor: COLORS.border,
              }}
            />

            {/* ===========================================================
                SAYISAL GÖSTERGELER
                =========================================================== */}

            <PanelHeading
              icon={<WorkspacePremiumIcon />}
              title={TT.blocks.counters}
              action={
                <StatusSwitch
                  checked={!!content.countersActive}
                  onChange={(event) =>
                    onField("countersActive", event.target.checked)
                  }
                  TT={TT}
                />
              }
            />

            <Grid
              container
              spacing={0}
              sx={cssGrid(
                {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                },
                1.5,
              )}
            >
              {metricItems.map((item) => (
                <Grid item xs={12} sm={6} lg={3} key={`input-${item.key}`}>
                  <MetricInput
                    label={TT.fields[item.key]}
                    value={content.counters?.[item.key] ?? 0}
                    onChange={(event) =>
                      onField(
                        `counters.${item.key}`,
                        Number(event.target.value),
                      )
                    }
                  />
                </Grid>
              ))}
            </Grid>

            <Divider
              sx={{
                my: {
                  xs: 3,
                  md: 4,
                },

                borderColor: COLORS.border,
              }}
            />

            {/* ===========================================================
                KİŞİLER
                =========================================================== */}

            <PanelHeading
              icon={<GroupsIcon />}
              title={TT.blocks.people}
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <StatusSwitch
                    checked={!!content.peopleActive}
                    onChange={(event) =>
                      onField("peopleActive", event.target.checked)
                    }
                    TT={TT}
                  />

                  <Button
                    onClick={addPerson}
                    startIcon={<AddIcon />}
                    variant="contained"
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 850,
                      boxShadow: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: {
                          xs: "none",
                          sm: "inline",
                        },
                      }}
                    >
                      {TT.labels.addPerson}
                    </Box>

                    <Box
                      component="span"
                      sx={{
                        display: {
                          xs: "inline",
                          sm: "none",
                        },
                      }}
                    >
                      {TT.labels.addPerson.split(" ").slice(-2).join(" ")}
                    </Box>
                  </Button>
                </Stack>
              }
            />

            {(content.people || []).length === 0 ? (
              <EmptyState
                icon={<GroupsIcon fontSize="large" />}
                text={TT.labels.emptyPeople}
              />
            ) : (
              <Grid
                container
                spacing={0}
                alignItems="stretch"
                sx={cssGrid(
                  {
                    xs: "minmax(0, 1fr)",
                    lg: "repeat(2, minmax(0, 1fr))",
                  },
                  2,
                )}
              >
                {(content.people || []).map((person, index) => (
                  <Grid
                    item
                    xs={12}
                    lg={6}
                    key={`person-editor-${index}`}
                    sx={{
                      display: "flex",
                      minWidth: 0,
                    }}
                  >
                    <PersonEditorCard
                      person={person}
                      index={index}
                      setPersonValue={setPersonValue}
                      removePerson={removePerson}
                      TT={TT}
                      version={version}
                      setVersion={setVersion}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            <Divider
              sx={{
                my: {
                  xs: 3,
                  md: 4,
                },

                borderColor: COLORS.border,
              }}
            />

            {/* ===========================================================
                GALERİ
                =========================================================== */}

            <PanelHeading
              icon={<CollectionsIcon />}
              title={TT.blocks.gallery}
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <StatusSwitch
                    checked={!!content.galleryActive}
                    onChange={(event) =>
                      onField("galleryActive", event.target.checked)
                    }
                    TT={TT}
                  />

                  <Button
                    onClick={addGalleryItem}
                    startIcon={<AddIcon />}
                    variant="contained"
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 850,
                      bgcolor: COLORS.orange,
                      color: COLORS.navy,
                      boxShadow: "none",
                      whiteSpace: "nowrap",

                      "&:hover": {
                        bgcolor: "#fbbf24",
                        boxShadow: "none",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: {
                          xs: "none",
                          sm: "inline",
                        },
                      }}
                    >
                      {TT.labels.addPhoto}
                    </Box>

                    <Box
                      component="span"
                      sx={{
                        display: {
                          xs: "inline",
                          sm: "none",
                        },
                      }}
                    >
                      {TT.labels.addPhoto.split(" ").slice(-2).join(" ")}
                    </Box>
                  </Button>
                </Stack>
              }
            />

            {(content.gallery || []).length === 0 ? (
              <EmptyState
                icon={<CollectionsIcon fontSize="large" />}
                text={TT.labels.emptyGallery}
              />
            ) : (
              <Grid
                container
                spacing={0}
                alignItems="stretch"
                sx={cssGrid(
                  {
                    xs: "minmax(0, 1fr)",
                    lg: "repeat(2, minmax(0, 1fr))",
                  },
                  2,
                )}
              >
                {(content.gallery || []).map((item, index) => (
                  <Grid
                    item
                    xs={12}
                    lg={6}
                    key={`gallery-editor-${index}`}
                    sx={{
                      display: "flex",
                      minWidth: 0,
                    }}
                  >
                    <GalleryEditorCard
                      item={item}
                      index={index}
                      setGalleryValue={setGalleryValue}
                      removeGalleryItem={removeGalleryItem}
                      TT={TT}
                      version={version}
                      setVersion={setVersion}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* ===========================================================
                KAYDET
                =========================================================== */}

            <Paper
              elevation={0}
              sx={{
                mt: 3.5,

                p: {
                  xs: 1.25,
                  sm: 1.5,
                },

                borderRadius: 3,
                border: `1px solid ${COLORS.border}`,
                background: "linear-gradient(90deg, #f8fafc, #eff6ff)",
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                alignItems={{
                  xs: "stretch",
                  sm: "center",
                }}
                justifyContent="space-between"
                spacing={1.5}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: COLORS.textSoft,
                    fontWeight: 650,

                    px: {
                      sm: 1,
                    },
                  }}
                >
                  {TT.editorIntro}
                </Typography>

                <MotionButton
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={saveToServer}
                  variant="contained"
                  size="large"
                  disabled={saving}
                  whileHover={
                    saving
                      ? {}
                      : {
                          scale: 1.015,
                        }
                  }
                  whileTap={
                    saving
                      ? {}
                      : {
                          scale: 0.985,
                        }
                  }
                  sx={{
                    minHeight: 50,

                    px: {
                      xs: 2,
                      sm: 3.5,
                    },

                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 900,
                    color: "#fff",
                    bgcolor: COLORS.navy,
                    boxShadow: "0 10px 25px rgba(11,31,58,.22)",

                    "&:hover": {
                      bgcolor: COLORS.navySoft,
                    },
                  }}
                >
                  {saving ? TT.labels.loading : TT.labels.save}
                </MotionButton>
              </Stack>
            </Paper>
          </Paper>

          {/* =============================================================
              ÖNİZLEME
              ============================================================= */}

          <Box
            sx={{
              mt: {
                xs: 3,
                md: 4.5,
              },
            }}
          >
            <PanelHeading
              icon={<VisibilityIcon />}
              title={TT.blocks.preview}
              subtitle={TT.previewIntro}
            />

            <Grid
              container
              spacing={0}
              alignItems="stretch"
              sx={cssGrid(
                {
                  xs: "minmax(0, 1fr)",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
                2,
              )}
            >
              {content.missionActive && (
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  sx={{
                    display: "flex",
                  }}
                >
                  <PreviewTextCard
                    title={TT.blocks.mission}
                    text={content.mission}
                    accent={COLORS.blue}
                    icon={<HubIcon fontSize="small" />}
                  />
                </Grid>
              )}

              {content.visionActive && (
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  sx={{
                    display: "flex",
                  }}
                >
                  <PreviewTextCard
                    title={TT.blocks.vision}
                    text={content.vision}
                    accent={COLORS.orange}
                    icon={<VisibilityIcon fontSize="small" />}
                  />
                </Grid>
              )}

              {content.historyActive && (
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={4}
                  sx={{
                    display: "flex",
                  }}
                >
                  <PreviewTextCard
                    title={TT.blocks.history}
                    text={content.history}
                    accent={COLORS.green}
                    icon={<LanguageIcon fontSize="small" />}
                  />
                </Grid>
              )}
            </Grid>

            {/* ===========================================================
                KİŞİLER ÖNİZLEME
                =========================================================== */}

            {content.peopleActive && (content.people || []).length > 0 && (
              <Box
                sx={{
                  mt: 4,
                }}
              >
                <PanelHeading icon={<GroupsIcon />} title={TT.blocks.people} />

                <Grid
                  container
                  spacing={0}
                  alignItems="stretch"
                  sx={cssGrid(
                    {
                      xs: "minmax(0, 1fr)",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(3, minmax(0, 1fr))",
                    },
                    2,
                  )}
                >
                  {(content.people || []).map((person, index) => {
                    const photoUrl = person.photoUrl || person.link || "";

                    return (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={4}
                        key={`person-preview-${index}`}
                        sx={{
                          display: "flex",
                          minWidth: 0,
                        }}
                      >
                        <MotionPaper
                          whileHover={{
                            y: -4,
                          }}
                          transition={{
                            type: "spring",
                            stiffness: 210,
                            damping: 22,
                          }}
                          sx={{
                            p: {
                              xs: 2,
                              sm: 2.5,
                            },

                            width: "100%",
                            borderRadius: 4,
                            border: `1px solid ${COLORS.border}`,
                            boxShadow: "0 12px 32px rgba(15,23,42,.06)",
                            display: "flex",

                            flexDirection: {
                              xs: "column",
                              sm: "row",
                            },

                            alignItems: {
                              xs: "center",
                              sm: "flex-start",
                            },

                            gap: 2,

                            textAlign: {
                              xs: "center",
                              sm: "left",
                            },
                          }}
                        >
                          <Avatar
                            src={bust(photoUrl, version)}
                            alt={person.name}
                            onClick={() =>
                              photoUrl && setOpenImg(bust(photoUrl, version))
                            }
                            sx={{
                              width: 92,
                              height: 92,
                              flexShrink: 0,
                              fontWeight: 900,
                              fontSize: "1.4rem",
                              bgcolor: COLORS.navy,
                              border: "4px solid #eff6ff",

                              cursor: photoUrl ? "zoom-in" : "default",
                            }}
                          >
                            {initials(person.name)}
                          </Avatar>

                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              width: "100%",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: COLORS.text,
                                fontSize: "1.05rem",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {person.name || "—"}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.25,
                                color: COLORS.blue,
                                fontWeight: 800,
                              }}
                            >
                              {person.role || "—"}
                            </Typography>

                            {person.desc && (
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 1,
                                  color: COLORS.textSoft,
                                  lineHeight: 1.65,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {person.desc}
                              </Typography>
                            )}

                            {photoUrl && (
                              <Tooltip title={TT.labels.copy}>
                                <IconButton
                                  size="small"
                                  onClick={() => copy(photoUrl)}
                                  sx={{
                                    mt: 0.75,
                                    bgcolor: COLORS.blueSoft,
                                  }}
                                >
                                  <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </MotionPaper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* ===========================================================
                GALERİ ÖNİZLEME
                =========================================================== */}

            {content.galleryActive && (content.gallery || []).length > 0 && (
              <Box
                sx={{
                  mt: 4,
                }}
              >
                <PanelHeading
                  icon={<CollectionsIcon />}
                  title={TT.blocks.gallery}
                />

                <Grid
                  container
                  spacing={0}
                  alignItems="stretch"
                  sx={cssGrid(
                    {
                      xs: "minmax(0, 1fr)",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(3, minmax(0, 1fr))",
                    },
                    2,
                  )}
                >
                  {(content.gallery || []).map((item, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      lg={4}
                      key={`gallery-preview-${index}`}
                      sx={{
                        display: "flex",
                        minWidth: 0,
                      }}
                    >
                      <MotionPaper
                        whileHover={{
                          y: -4,
                        }}
                        transition={{
                          type: "spring",
                          stiffness: 210,
                          damping: 22,
                        }}
                        sx={{
                          width: "100%",
                          borderRadius: 4,
                          overflow: "hidden",
                          border: `1px solid ${COLORS.border}`,
                          boxShadow: "0 12px 32px rgba(15,23,42,.06)",
                          bgcolor: "#fff",
                        }}
                      >
                        {item.url ? (
                          <MotionImg
                            src={bust(item.url, version)}
                            alt={item.name || TT.labels.galleryImage}
                            onClick={() => setOpenImg(bust(item.url, version))}
                            whileHover={{
                              scale: 1.025,
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              height: 230,
                              objectFit: "cover",
                              cursor: "zoom-in",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 230,
                              display: "grid",
                              placeItems: "center",
                              color: "#94a3b8",
                              bgcolor: "#e2e8f0",
                            }}
                          >
                            <CollectionsIcon
                              sx={{
                                fontSize: 52,
                              }}
                            />
                          </Box>
                        )}

                        <Box
                          sx={{
                            p: 2.25,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 950,
                              color: COLORS.text,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {item.name || "—"}
                          </Typography>

                          {item.desc && (
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.75,
                                color: COLORS.textSoft,
                                lineHeight: 1.6,
                              }}
                            >
                              {item.desc}
                            </Typography>
                          )}

                          {item.url && (
                            <Tooltip title={TT.labels.copy}>
                              <IconButton
                                size="small"
                                onClick={() => copy(item.url)}
                                sx={{
                                  mt: 1,
                                  bgcolor: COLORS.orangeSoft,
                                }}
                              >
                                <ContentCopyIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </MotionPaper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* ===============================================================
          BÜYÜK GÖRSEL PENCERESİ
          =============================================================== */}

      <Dialog
        open={!!openImg}
        onClose={() => setOpenImg(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: {
              xs: 2,
              sm: 3,
            },

            overflow: "hidden",
            bgcolor: "#020617",
          },
        }}
      >
        <IconButton
          onClick={() => setOpenImg(null)}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            zIndex: 2,
            bgcolor: "rgba(2,6,23,.72)",
            color: "#fff",

            "&:hover": {
              bgcolor: "rgba(2,6,23,.9)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{
            p: 0,
            bgcolor: "#020617",
          }}
        >
          {openImg && (
            <img
              src={openImg}
              alt="large"
              style={{
                width: "100%",
                maxHeight: "88vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ===============================================================
          BİLDİRİM
          =============================================================== */}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnack((current) => ({
            ...current,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() =>
            setSnack((current) => ({
              ...current,
              open: false,
            }))
          }
          severity={snack.severity}
          variant="filled"
          sx={{
            fontWeight: 800,
          }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
